import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Button, Icon } from '../../../pages/DesignSystem';
import { useGeolocation } from '../../../hooks/useGeolocation';
import { useReverseGeocode, ReverseGeocodeResult } from '../../../hooks/useReverseGeocode';
import './LocationPickerModal.css';

export interface LocationResult {
  latitude: number;
  longitude: number;
  location: string;
  locationName: string;
  geohash: string;
}

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (location: LocationResult) => void;
  initialPosition?: { lat: number; lng: number };
  defaultCenter?: { lat: number; lng: number };
}

// Default center: Hiriketiya Beach, Sri Lanka
const DEFAULT_CENTER = { lat: 5.9650, lng: 80.7100 };
const DEFAULT_ZOOM = 14;
const GPS_ZOOM = 17;

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: true,
  gestureHandling: 'greedy',
};

/**
 * LocationPickerModal - Interactive map modal for selecting a location
 *
 * Features:
 * - Click on map to place marker
 * - Drag marker to adjust position
 * - GPS button to use current location
 * - Reverse geocoding to get address from coordinates
 */
export const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialPosition,
  defaultCenter = DEFAULT_CENTER,
}) => {
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(
    initialPosition || null
  );
  const [address, setAddress] = useState('');
  const [locationName, setLocationName] = useState('');
  const [geohash, setGeohash] = useState('');
  const [mapCenter, setMapCenter] = useState(initialPosition || defaultCenter);
  const [hasInteracted, setHasInteracted] = useState(false);

  const { getPosition, loading: geoLoading, error: geoError, clearError } = useGeolocation();
  const { reverseGeocode, loading: geocodeLoading } = useReverseGeocode();

  const mapRef = useRef<google.maps.Map | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialPosition) {
        setMarkerPosition(initialPosition);
        setMapCenter(initialPosition);
        // Reverse geocode initial position
        reverseGeocode(initialPosition.lat, initialPosition.lng).then((result) => {
          if (result) {
            setAddress(result.formattedAddress);
            setLocationName(result.locationName);
            setGeohash(result.geohash);
          }
        });
      } else {
        setMarkerPosition(null);
        setAddress('');
        setLocationName('');
        setGeohash('');
        setMapCenter(defaultCenter);
        setHasInteracted(false);
      }
      clearError();
    }
  }, [isOpen, initialPosition, defaultCenter, reverseGeocode, clearError]);

  // Handle reverse geocoding result
  const handleGeocodeResult = useCallback((result: ReverseGeocodeResult | null) => {
    if (result) {
      setAddress(result.formattedAddress);
      setLocationName(result.locationName);
      setGeohash(result.geohash);
    }
  }, []);

  // When marker is dragged
  const handleMarkerDragEnd = useCallback(async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });

    const result = await reverseGeocode(lat, lng);
    handleGeocodeResult(result);
  }, [reverseGeocode, handleGeocodeResult]);

  // Click on map = place/move marker
  const handleMapClick = useCallback(async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });

    const result = await reverseGeocode(lat, lng);
    handleGeocodeResult(result);
  }, [reverseGeocode, handleGeocodeResult]);

  // GPS button click
  const handleGPSClick = useCallback(async () => {
    const position = await getPosition();
    if (position) {
      setMarkerPosition(position);
      setMapCenter(position);
      mapRef.current?.panTo(position);
      mapRef.current?.setZoom(GPS_ZOOM);

      const result = await reverseGeocode(position.lat, position.lng);
      handleGeocodeResult(result);
    }
  }, [getPosition, reverseGeocode, handleGeocodeResult]);

  // Hide hint when user starts interacting with the map
  const handleMapDragStart = useCallback(() => {
    setHasInteracted(true);
  }, []);

  // Confirm selection
  const handleConfirm = useCallback(() => {
    if (!markerPosition || !address) return;

    onConfirm({
      latitude: markerPosition.lat,
      longitude: markerPosition.lng,
      location: address,
      locationName: locationName,
      geohash: geohash,
    });
    onClose();
  }, [markerPosition, address, locationName, geohash, onConfirm, onClose]);

  const isLoading = geoLoading || geocodeLoading;
  const canConfirm = markerPosition && address && !isLoading;

  // Handle ESC key and body scroll
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="location-picker-modal__overlay"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="location-picker-modal">
        <div className="location-picker-modal__content">
        {/* Header */}
        <div className="location-picker-modal__header">
          <h2 className="location-picker-modal__title">
            <Icon name="MapPin" size={20} />
            Pick Event Location
          </h2>
          <button
            type="button"
            className="location-picker-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Error message */}
        {geoError && (
          <div className="location-picker-modal__error">
            <Icon name="AlertCircle" size={16} />
            <span>{geoError}</span>
          </div>
        )}

        {/* Map */}
        <div className="location-picker-modal__map">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={initialPosition ? DEFAULT_ZOOM + 1 : DEFAULT_ZOOM}
            onClick={handleMapClick}
            onLoad={(map) => { mapRef.current = map; }}
            onDragStart={handleMapDragStart}
            options={mapOptions}
          >
            {markerPosition && (
              <Marker
                position={markerPosition}
                draggable
                onDragEnd={handleMarkerDragEnd}
                animation={google.maps.Animation.DROP}
              />
            )}
          </GoogleMap>

          {/* GPS floating button */}
          <button
            type="button"
            className="location-picker-modal__gps-btn"
            onClick={handleGPSClick}
            disabled={geoLoading}
            title="Use my location"
          >
            <Icon name={geoLoading ? 'Loader' : 'Locate'} size={20} />
          </button>

          {/* Map overlay hint - hidden when user interacts or places marker */}
          {!markerPosition && !hasInteracted && (
            <div className="location-picker-modal__map-hint">
              <Icon name="MousePointer" size={24} />
              <span>Click on the map to select a location</span>
            </div>
          )}
        </div>

        {/* Address preview */}
        <div className="location-picker-modal__address">
          <Icon name="MapPin" size={16} />
          <span>
            {geocodeLoading
              ? 'Loading address...'
              : address || 'No location selected'}
          </span>
        </div>

        {/* Confirm button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="location-picker__confirm-btn"
        >
          {isLoading ? 'Loading...' : 'Confirm this location'}
        </Button>
        </div>
      </div>
    </>
  );
};

export default LocationPickerModal;
